import type { LandingContent } from "../schema";
import type { BuiltInShowcase } from "../showcase";

/** Shown in "Recent work" until an admin picks other websites. */
export const webDevelopmentSanDiegoShowcase: BuiltInShowcase = [
  ["staysharpsd.com", "Stay Sharp Barbershop", "Business website"],
  ["plushrepairs.com", "Plush Repairs", "Business website"],
  ["rugs2riches.vip", "Rugs 2 Riches", "Business website"],
  ["lateefahjackson.com", "Lateefah Jackson", "Author website"],
  ["sermonology.com", "Sermonology", "Web app"],
  ["sendshare.xyz", "SendShare", "Web app"],
];

export const webDevelopmentSanDiegoDefaults: LandingContent = {
  phone: "858-333-5005",
  seo: {
    title: "Web Development in San Diego | Smartly Technologies",
    description:
      "Custom websites, online stores, and web apps for San Diego and Southern California businesses. Book a free 30-minute call for a clear plan and price.",
  },
  header: { callLabel: "Call us" },
  variants: {
    default: {
      eyebrow: "WEB DEVELOPMENT · SAN DIEGO COUNTY",
      highlight: "Web Development",
      rest: "for San Diego Businesses",
    },
    "web-design": {
      eyebrow: "WEB DESIGN · SAN DIEGO COUNTY",
      highlight: "Web Design",
      rest: "for San Diego Businesses",
    },
    "web-apps": {
      eyebrow: "WEB APP DEVELOPMENT · SAN DIEGO COUNTY",
      highlight: "Custom Web Apps",
      rest: "for San Diego Businesses",
    },
    ecommerce: {
      eyebrow: "ECOMMERCE DEVELOPMENT · SAN DIEGO COUNTY",
      highlight: "Online Stores",
      rest: "for San Diego Businesses",
    },
  },
  hero: {
    intro:
      "We design and build fast, modern websites, online stores, and web apps. Book a free 30-minute call and get a clear plan, timeline, and price before any work starts.",
    introMobile:
      "Websites, online stores, and web apps. Book a free 30-minute call and get a clear plan and price before any work starts.",
    checklist: [
      "Most business sites launch in 4 to 8 weeks",
      "SEO, speed, and mobile built into every site",
      "Easy to update yourself, with support after launch",
    ],
    callPrompt: "Prefer to talk now?",
  },
  booking: {
    title: "Book your free 30-minute call",
    subtitle: "Tell us where to reach you. Then choose a time that works.",
    button: "Next: Pick a Time",
    reassurance: "Free call. No obligation. Your details stay private.",
  },
  trust: [
    { title: "Free 30-minute call", text: "No cost and no obligation" },
    { title: "Price before work starts", text: "A written proposal with scope and cost" },
    { title: "Local team", text: "Chula Vista, serving San Diego County" },
    { title: "30+ years in technology", text: "Websites, apps, and IT under one roof" },
  ],
  work: {
    eyebrow: "RECENT WORK",
    title: "Real sites we have built",
    subtitle: "Every project below is live today. Yours could be next.",
    items: [],
  },
  process: {
    eyebrow: "HOW IT WORKS",
    title: "Three steps from first call to launch",
    steps: [
      {
        title: "Book a free call",
        text: "Pick a time that works for you. We meet for 30 minutes by video call and talk through what you need.",
      },
      {
        title: "Get your plan and price",
        text: "We send a written proposal with the scope, timeline, and cost. You decide with no pressure.",
      },
      {
        title: "We build and launch",
        text: "You see progress along the way. Most business sites go live in 4 to 8 weeks, and we stay on for support.",
      },
    ],
  },
  cta: { label: "Book My Free Call" },
  questions: {
    eyebrow: "QUESTIONS",
    title: "What people ask before they book",
    items: [
      {
        q: "How long does it take to build a website?",
        a: "Most business websites take 4 to 8 weeks. Online stores and web apps usually take 8 to 16 weeks. You get a clear timeline before we start.",
      },
      {
        q: "How much does a website cost?",
        a: "It depends on what you need. After your free call, we send a written proposal with the full scope and price, so you know the cost before any work begins.",
      },
      {
        q: "Can I update the site myself?",
        a: "Yes. We set up an easy content editor and show you how to change text, photos, and posts on your own.",
      },
      {
        q: "Is SEO included?",
        a: "Yes. Every site we build has clean code, fast load times, mobile-friendly pages, and the right titles and tags for search.",
      },
      {
        q: "Do you help after launch?",
        a: "Yes. Our support plans cover security updates, backups, content changes, and help when you need it.",
      },
      {
        q: "Can you build an online store or a web app?",
        a: "Yes. We build online stores with secure checkout, and custom web apps like the ones in our portfolio.",
      },
    ],
  },
  finalCta: {
    title: "Ready to plan your website?",
    text: "Book a free 30-minute call, or talk to us right now.",
  },
};
